import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  AutoAwesome as AutoAwesomeIcon,
  BarChart as BarChartIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import AgencyFormDialog from '../dialogs/AgencyFormDialog';
import SMBFormDialog from '../dialogs/SMBFormDialog';

// Lazy load tab components for better performance
const DashboardTab = lazy(() => import('./DashboardTab'));
const AgenciesTab = lazy(() => import('./AgenciesTab'));
const SmbsTab = lazy(() => import('./SmbsTab'));
const LeadsTab = lazy(() => import('./LeadsTab'));
const TemplatesTab = lazy(() => import('./TemplatesTab'));
const FlowsTab = lazy(() => import('./FlowsTab'));
const BookingsTab = lazy(() => import('./BookingsTab'));
const AITab = lazy(() => import('./AITab'));
const AIUsageTab = lazy(() => import('./AIUsageTab'));
const BusinessDataTab = lazy(() => import('./BusinessDataTab'));

interface DashboardStats {
  overview: {
    total_agencies: number;
    total_smbs: number;
    active_smbs: number;
    total_leads: number;
    total_messages: number;
    total_conversations: number;
    total_bookings: number;
    total_templates: number;
    total_flows: number;
    active_flows: number;
  };
  recent_activity: {
    leads_24h: number;
    messages_24h: number;
  };
  breakdowns: {
    lead_status: Record<string, number>;
    smb_status: Record<string, number>;
  };
  system_health: string;
}

interface Agency {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone?: string;
  website?: string;
  plan: string;
  status: string;
  created_at: string;
  smb_count: number;
}

interface SMB {
  id: string;
  name: string;
  slug: string;
  business_type: string;
  industry: string;
  owner_name: string;
  owner_email: string;
  phone: string;
  website?: string;
  status: string;
  agency_id: string;
  whatsapp_enabled: boolean;
  instagram_enabled: boolean;
  created_at: string;
  lead_count: number;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  email?: string;
  source: string;
  status: string;
  smb_id: string;
  created_at: string;
}

interface Template {
  id: string;
  name: string;
  category: string;
  content: string;
  status: string;
  smb_id: string;
  created_at: string;
}

interface Flow {
  id: string;
  name: string;
  description?: string;
  trigger_event: string;
  status: string;
  smb_id: string;
  steps_count: number;
  created_at: string;
}

interface Booking {
  id: string;
  service_name: string;
  scheduled_at: string;
  status: string;
  notes?: string;
  smb_id: string;
  lead_id: string;
  created_at: string;
}

const AdminDashboard: React.FC = () => {
  const { admin, isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [smbs, setSmbs] = useState<SMB[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'agency' | 'smb' | 'lead' | 'template' | 'flow' | 'booking'>('agency');
  const [selectedItem, setSelectedItem] = useState<Agency | SMB | Lead | Template | Flow | Booking | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Redirecting to login...
        </Typography>
      </Box>
    );
  }

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      const response = await fetch('/api/v1/admin/dashboard', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch dashboard stats');
      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard stats');
    }
  };

  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      const response = await fetch('/api/v1/admin/agencies', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch agencies');
      const data = await response.json();
      setAgencies(data.agencies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch agencies');
    }
  };

  const fetchSmbs = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      const response = await fetch('/api/v1/admin/smbs', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch SMBs');
      const data = await response.json();
      setSmbs(data.smbs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch SMBs');
    }
  };

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      const response = await fetch('/api/v1/admin/leads', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch leads');
      const data = await response.json();
      setLeads(data.leads);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch leads');
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      const response = await fetch('/api/v1/admin/templates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch templates');
      const data = await response.json();
      setTemplates(data.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch templates');
    }
  };

  const fetchFlows = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      const response = await fetch('/api/v1/admin/flows', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch flows');
      const data = await response.json();
      setFlows(data.flows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch flows');
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      const response = await fetch('/api/v1/admin/bookings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      setBookings(data.bookings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch bookings');
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchAgencies(),
        fetchSmbs(),
        fetchLeads(),
        fetchTemplates(),
        fetchFlows(),
        fetchBookings()
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (type: 'agency' | 'smb' | 'lead' | 'template' | 'flow' | 'booking', item?: Agency | SMB | Lead | Template | Flow | Booking | null) => {
    setDialogType(type);
    setSelectedItem(item ?? null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
    setFormLoading(false);
  };

  // Accept "any" here to avoid mismatch with dialog-internal types which are not exported.
  const handleSaveAgency = async (data: any) => {
    setFormLoading(true);
    try {
      const isEditing = selectedItem && 'slug' in selectedItem;
      const url = isEditing ? `/api/v1/admin/agencies/${(selectedItem as Agency).id}` : '/api/v1/admin/agencies';
      const method = isEditing ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('admin_access_token')}` },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as any).detail || 'Failed to create/update agency');
      }

      await fetchAgencies();
      handleCloseDialog();
      setError(null);
      console.log(`Agency ${isEditing ? 'updated' : 'created'} successfully`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save agency');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSaveSMB = async (data: any) => {
    setFormLoading(true);
    try {
      const isEditing = selectedItem && 'business_type' in selectedItem;
      const url = isEditing ? `/api/v1/admin/smbs/${(selectedItem as SMB).id}` : '/api/v1/admin/smbs';
      const method = isEditing ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('admin_access_token')}` },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error((errorData as any).detail || 'Failed to create/update SMB');
      }

      await fetchSmbs();
      handleCloseDialog();
      setError(null);
      console.log(`SMB ${isEditing ? 'updated' : 'created'} successfully`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save SMB');
    } finally {
      setFormLoading(false);
    }
  };

  const renderDialog = () => {
    if (!dialogOpen) return null;

    if (dialogType === 'agency') {
      return (
        <AgencyFormDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSaveAgency}
          // dialog's types are internal — use any to satisfy prop
          editingAgency={selectedItem && 'slug' in selectedItem ? (selectedItem as any) : undefined}
          loading={formLoading}
        />
      );
    }

    if (dialogType === 'smb') {
      return (
        <SMBFormDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSaveSMB}
          editingSMB={selectedItem && 'business_type' in selectedItem ? (selectedItem as any) : undefined}
          loading={formLoading}
        />
      );
    }

    return (
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedItem ? `View ${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}` : `Add ${dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedItem ? (
              <Box>
                <pre>{JSON.stringify(selectedItem, null, 2)}</pre>
              </Box>
            ) : (
              <Typography>
                Add new {dialogType} form would go here
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {!selectedItem && (
            <Button variant="contained" onClick={handleCloseDialog}>
              Create
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        p: 2,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              FunnelAutomation Admin
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Welcome, {admin?.name || 'Admin'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{
            borderColor: 'rgba(255,255,255,0.5)',
            color: 'white',
            '&:hover': {
              borderColor: 'white',
              bgcolor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          Logout
        </Button>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Dashboard" icon={<DashboardIcon />} />
        <Tab label="Agencies" icon={<BusinessIcon />} />
        <Tab label="SMBs" icon={<BusinessIcon />} />
        <Tab label="Leads" icon={<PeopleIcon />} />
        <Tab label="Templates" icon={<MessageIcon />} />
        <Tab label="Flows" icon={<AssessmentIcon />} />
        <Tab label="Bookings" icon={<ScheduleIcon />} />
        <Tab label="AI Settings" icon={<AutoAwesomeIcon />} />
        <Tab label="AI Usage" icon={<BarChartIcon />} />
        <Tab label="Business Data" icon={<StorageIcon />} />
      </Tabs>

      <Suspense fallback={<CircularProgress />}>
        {activeTab === 0 && <DashboardTab stats={stats} loading={loading} error={error} onRefresh={refreshData} />}
        {activeTab === 1 && (
          <AgenciesTab agencies={agencies} onAdd={() => handleOpenDialog('agency')} onEdit={(item: Agency) => handleOpenDialog('agency', item)} />
        )}
        {activeTab === 2 && (
          <SmbsTab smbs={smbs} onAdd={() => handleOpenDialog('smb')} onEdit={(item: SMB) => handleOpenDialog('smb', item)} />
        )}
        {activeTab === 3 && (
          <LeadsTab leads={leads} smbs={smbs} onAdd={() => handleOpenDialog('lead')} onEdit={(item: Lead) => handleOpenDialog('lead', item)} />
        )}
        {activeTab === 4 && (
          <TemplatesTab templates={templates} smbs={smbs} onAdd={() => handleOpenDialog('template')} onEdit={(item: Template) => handleOpenDialog('template', item)} />
        )}
        {activeTab === 5 && (
          <FlowsTab flows={flows} smbs={smbs} onAdd={() => handleOpenDialog('flow')} onEdit={(item: Flow) => handleOpenDialog('flow', item)} />
        )}
        {activeTab === 6 && (
          <BookingsTab
            bookings={bookings}
            smbs={smbs}
            leads={leads}
            onAdd={() => handleOpenDialog('booking')}
            onEdit={(item: Booking) => handleOpenDialog('booking', item)}
          />
        )}
        {activeTab === 7 && <AITab smbs={smbs} />}
        {activeTab === 8 && <AIUsageTab />}
        {activeTab === 9 && <BusinessDataTab smbs={smbs} />}
      </Suspense>

      {renderDialog()}
    </Box>
  );
};

export default AdminDashboard;
